import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";

import { TranslateService } from '@ngx-translate/core';

import {CustomValidators} from "@commons/validators/validators";
import {SetPasswordRequest} from "@commons/models/remote.request/SetPasswordRequest";
import { WebAccessService } from '@commons/services/web.access/web-access.service';

import {NewNavigationService} from "@shared/services/new-navigation.service";
import {BaseComponent} from "@shared/components/base-component";

@Component({
  selector: 'app-set-new-password',
  templateUrl: './set-new-password.component.html',
  styles: []
})
export class SetNewPasswordComponent extends BaseComponent implements OnInit, OnDestroy {

  private readonly requiredErrorKey = 'required';
  private readonly weekPasswordErrorKey = 'weekPassword';
  private readonly passwordsNotEqualErrorKey = 'passwordsNotEqual';

  fieldIsRequiredErrorMessage = '';
  passwordRequirementsErrorMessage = '';
  passwordsNotEqualErrorMessage = '';

  form!: FormGroup;
  passwordResetToken?: string | null;
  isLoading = false;
  showError = false;
  inputType = 'password';
  isRawPasswordVisible = false;
  showTokenError = false;
  showTokenExpiredError = false;

  newPasswordCtrl = new FormControl('',
    Validators.compose([Validators.required, CustomValidators.passwordStrength, this.passwordsEqualValidator()]));

  newPasswordRepeatCtrl = new FormControl('',
    Validators.compose([Validators.required, CustomValidators.passwordStrength, this.passwordsEqualValidator()]));

  get isVisibleShowPasswordButton(): boolean {
    return this.newPasswordCtrl.value != null && this.newPasswordCtrl.value.length > 0;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private navigationService: NewNavigationService,
    private webAccessService: WebAccessService,
    private translateService: TranslateService
  ) {
    super();
    this.setTranslations();
  }

  private setTranslations() {
    this.translateService.get('errors.field-is-required').subscribe(t => this.fieldIsRequiredErrorMessage = t);
    this.translateService.get('password-change-shared.password-requirements-error').subscribe(t => this.passwordRequirementsErrorMessage = t);
    this.translateService.get('password-change-shared.passwords-not-equal-error').subscribe(t => this.passwordsNotEqualErrorMessage = t);
  }

  ngOnInit(): void {
    this.createForm();
    this.subscribeToFormValueChanges();
    this.setAndCheckToken();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  getErrorMessageElementId(controlName: string, control: AbstractControl): string {
    let errorName = '';

    if (control.hasError(this.requiredErrorKey))
      errorName = this.requiredErrorKey;
    else if (control.hasError(this.weekPasswordErrorKey))
      errorName = this.weekPasswordErrorKey;
    else if (control.hasError(this.passwordsNotEqualErrorKey))
      errorName = this.passwordsNotEqualErrorKey;

    return `${controlName}-error-${errorName}`;
  }

  getEmailErrorMessage(control: AbstractControl): string {
    if (control.hasError(this.requiredErrorKey))
      return this.fieldIsRequiredErrorMessage;

    if (control.hasError(this.weekPasswordErrorKey))
      return this.passwordRequirementsErrorMessage;

    if (control.hasError(this.passwordsNotEqualErrorKey))
      return this.passwordsNotEqualErrorMessage;

    return "";
  }

  onShowPasswords() {
    this.isRawPasswordVisible = true;
    this.inputType = 'text';
  }

  onHidePasswords() {
    this.isRawPasswordVisible = false;
    this.inputType = 'password';
  }

  onSave() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.showError = true;
      return;
    }

    this.showError = false;
    this.isLoading = true;

    this.setNewPassword();
  }

  private setNewPassword() {
    const request = {
      PasswordResetToken: this.passwordResetToken,
      NewPassword: this.newPasswordCtrl.value
    } as SetPasswordRequest;

    this.webAccessService.SetProfileUserPassword(request).subscribe({
      next: () => this.afterSetPassword(),
      error: () => this.afterSetPasswordError(),
    });
  }

  private afterSetPasswordError() {
    this.isLoading = false;
    this.showTokenExpiredError = true;
  }

  private afterSetPassword() {
    this.isLoading = false;
    this.navigationService.toSetNewPasswordConfirmation();
  }

  passwordsEqualValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const arePasswordsEqual =
        this.form != null &&
        this.form.controls != null &&
        this.newPasswordCtrl.value !== this.newPasswordRepeatCtrl.value;

      const result = arePasswordsEqual ? {passwordsNotEqual: true} : null;

      return result;
    };
  }

  private subscribeToFormValueChanges() {
    this.addToUnsubscribe(this.form.valueChanges.subscribe(() => this.arePasswordsNotEqual()));
  }

  private arePasswordsNotEqual() {
    const notEqual = this.newPasswordCtrl.value !== this.newPasswordRepeatCtrl.value;

    this.newPasswordCtrl.setErrors({passwordsNotEqual: notEqual ?? null});
    this.newPasswordCtrl.updateValueAndValidity({emitEvent: false});

    this.newPasswordRepeatCtrl.setErrors({passwordsNotEqual: notEqual ?? null});
    this.newPasswordRepeatCtrl.updateValueAndValidity({emitEvent: false});
  }

  private setAndCheckToken() {
    this.passwordResetToken = this.activatedRoute.snapshot.queryParamMap.get('PasswordResetToken');
    this.showTokenError = this.passwordResetToken == null || this.passwordResetToken.length != 36;
  }

  private createForm() {
    this.form = this.formBuilder.group({
      newPassword: this.newPasswordCtrl,
      newPasswordRepeat: this.newPasswordRepeatCtrl,
    });
  }

}
